import Box from '@zenbusiness/zui/Box';
import CircularProgress from '@zenbusiness/zui/CircularProgress';
import Grid from '@zenbusiness/zui/Grid';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import useBreakpoints from '#client/hooks/useBreakpoints';

import { ButtonStyled, IconWrapperStyled } from './Card.styes';

export default function Card({
  title,
  description,
  image,
  actionButton,
  markdoneButton = null,
  disabled = false,
  loading = false,
  removing = false
}) {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  return (
    <Stack gap={{ xs: '16px', md: '24px' }}>
      <Stack
        direction="row"
        gap="16px"
      >
        <Box
          height={{ xs: 48, md: 64 }}
          width={{ xs: 48, md: 64 }}
        >
          <SharedAsset
            alt=""
            src={image}
            width={isDesktop ? 64 : 48}
          />
        </Box>
        <Stack
          direction="column"
          flex={1}
          gap="4px"
        >
          <Text
            color="primary"
            size="lg"
            weight="medium"
          >
            {title}
          </Text>
          <Text
            color="primary"
            size="xs"
          >
            {description}
          </Text>
        </Stack>
      </Stack>
      <Grid
        container
        mb="1px"
        spacing={{ xs: '12px' }}
      >
        {markdoneButton && (
          <Grid
            item
            xs={actionButton ? 6 : 12}
          >
            <ButtonStyled
              color="primary"
              disabled={disabled || loading}
              onClick={markdoneButton.onClick}
              removing={removing}
              variant="outlined"
            >
              <span>{markdoneButton.label}</span>
            </ButtonStyled>
          </Grid>
        )}
        {actionButton && (
          <Grid
            item
            xs={markdoneButton ? 6 : 12}
          >
            <ButtonStyled
              color="secondary"
              disabled={disabled || loading}
              endIcon={
                loading && (
                  <CircularProgress
                    color="inherit"
                    size={16}
                  />
                )
              }
              onClick={actionButton.onClick}
              removing={removing}
              variant="contained"
            >
              <span>
                {removing ? (
                  <IconWrapperStyled>
                    <SharedAsset
                      alt=""
                      src="/s2/icon/check-circle-fill.svg"
                      width={isDesktop ? 24 : 20}
                    />
                  </IconWrapperStyled>
                ) : (
                  actionButton.label
                )}
              </span>
            </ButtonStyled>
          </Grid>
        )}
      </Grid>
    </Stack>
  );
}

Card.propTypes = {
  /**
   * The title of the card.
   */
  title: PropTypes.string.isRequired,
  /**
   * The description of the card.
   */
  description: PropTypes.string.isRequired,
  /**
   * The image of the card.
   */
  image: PropTypes.string.isRequired,
  /**
   * The action button of the card.
   */
  actionButton: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
  }).isRequired,
  /**
   * The markdone button of the card.
   */
  markdoneButton: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
  }),
  /**
   * The removing of the card.
   */
  removing: PropTypes.bool,
  /**
   * The disabled of the card.
   */
  disabled: PropTypes.bool,
  /**
   * The loading of the card.
   */
  loading: PropTypes.bool
};
